import React from 'react';
import { Controller, Control } from 'react-hook-form';
import {
    Autocomplete,
    Checkbox,
    TextField,
    FormControl,
    Chip,
    AutocompleteChangeReason,
    AutocompleteChangeDetails,
    AutocompleteRenderOptionState,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

export interface BaseOptionShape {
    [key: string]: any;
}

export type OptionValue = string | number | boolean | { id: number };

const getComparableOptionValue = (value: OptionValue) =>
    value && typeof value === 'object' && 'id' in value ? value.id : value;

const compareOptionValues = (left: OptionValue, right: OptionValue) => {
    return getComparableOptionValue(left) === getComparableOptionValue(right);
};

export interface FormMultiAutocompleteProps<T extends BaseOptionShape> {
    id?: string;
    name: string;
    control: Control<any>;
    options: T[];

    label?: string;
    customError?: string;

    emptyPlaceholder?: string;
    placeholder?: string;

    fontSize?: string;

    value?: T[];
    onChange?: (
        event: React.SyntheticEvent,
        value: T[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<T>,
    ) => void;

    mapFormValues?: (selected: T[]) => OptionValue[];

    getOptionValue?: (opt: T) => OptionValue;
    getOptionLabel?: (opt: T) => string;

    disableCloseOnSelect?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    getOptionDisabled?: (option: T) => boolean;
    isOptionEqualToValue?: (option: T, value: T) => boolean;

    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: T,
        state: AutocompleteRenderOptionState,
    ) => React.ReactNode;

    renderTags?: (
        value: T[],
        getTagProps: ({ index }: { index: number }) => {
            key: number;
            className: string;
            onDelete: React.MouseEventHandler<SVGSVGElement | HTMLSpanElement>;
        },
    ) => React.ReactNode;

    /**
     * The component used to render the body of the popup.
     * @default Paper
     */
    PaperComponent?: React.JSXElementConstructor<
        React.HTMLAttributes<HTMLElement>
    >;
}

function FormMultiAutocompleteInner<T extends BaseOptionShape>({
    id,
    name,
    control,
    label,
    options,
    customError,
    emptyPlaceholder,
    placeholder,
    value: externalValue,
    onChange: externalOnChange,
    mapFormValues,
    fontSize = '14px',

    getOptionValue,
    getOptionLabel,

    disableCloseOnSelect = true,
    disabled,
    fullWidth = true,
    getOptionDisabled,
    isOptionEqualToValue,

    renderOption,
    renderTags,
    PaperComponent,
}: FormMultiAutocompleteProps<T>) {
    const fallbackGetOptionValue = React.useCallback(
        (opt: T): OptionValue => {
            if (getOptionValue) return getOptionValue(opt);
            if (opt && typeof opt === 'object') {
                if ((opt as any).value != null)
                    return (opt as any).value as OptionValue;
                if ((opt as any).id != null)
                    return (opt as any).id as OptionValue;
            }
            return '' as OptionValue;
        },
        [getOptionValue],
    );

    const fallbackGetOptionLabel = React.useCallback(
        (opt: T): string => {
            if (getOptionLabel) return getOptionLabel(opt);
            if (opt && typeof opt === 'object') {
                if (opt.label != null) return String(opt.label);
                if (opt.name != null) return String(opt.name);
            }
            return '';
        },
        [getOptionLabel],
    );

    const fallbackIsEqual = React.useCallback(
        (opt: T, val: T) => {
            if (isOptionEqualToValue) return isOptionEqualToValue(opt, val);
            return compareOptionValues(
                fallbackGetOptionValue(opt),
                fallbackGetOptionValue(val),
            );
        },
        [isOptionEqualToValue, fallbackGetOptionValue],
    );

    const optionTextSx = {
        opacity: 0.9,
        fontSize,
        fontFamily: 'Inter',
        lineHeight: 1.4,
        display: 'flex',
        alignItems: 'center',
    } as const;

    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { onChange: rhfOnChange, value: rhfValue, ref },
                fieldState: { error },
            }) => {
                let selectedOptions: T[];
                if (externalValue) {
                    selectedOptions = externalValue;
                } else {
                    const currentArray = Array.isArray(rhfValue)
                        ? (rhfValue as OptionValue[])
                        : [];
                    selectedOptions = currentArray
                        .map((storedVal: any) =>
                            options.find((opt) =>
                                compareOptionValues(
                                    fallbackGetOptionValue(opt),
                                    storedVal,
                                ),
                            ),
                        )
                        .filter(Boolean) as T[];
                }

                const activePlaceholder =
                    selectedOptions.length === 0
                        ? placeholder || emptyPlaceholder || ''
                        : '';

                const hasError = !!error || !!customError;
                const helperText =
                    (error && error.message) || customError || '';

                return (
                    <FormControl
                        fullWidth={fullWidth}
                        size='small'
                        error={hasError}
                        disabled={disabled}
                    >
                        <Autocomplete<T, true, true, false>
                            id={id}
                            multiple
                            disableCloseOnSelect={disableCloseOnSelect}
                            disabled={disabled}
                            options={options}
                            value={selectedOptions}
                            fullWidth={fullWidth}
                            size='small'
                            getOptionLabel={(opt) =>
                                fallbackGetOptionLabel(opt)
                            }
                            isOptionEqualToValue={(opt, val) =>
                                fallbackIsEqual(opt, val)
                            }
                            getOptionDisabled={getOptionDisabled}
                            onChange={(event, newValue, reason, details) => {
                                const normalizedValues = mapFormValues
                                    ? mapFormValues(newValue)
                                    : newValue.map((opt) =>
                                          fallbackGetOptionValue(opt),
                                      );

                                rhfOnChange(normalizedValues);

                                if (externalOnChange) {
                                    externalOnChange(
                                        event,
                                        newValue,
                                        reason,
                                        details,
                                    );
                                }
                            }}
                            renderTags={
                                renderTags
                                    ? (tagValue, getTagProps) =>
                                          renderTags(
                                              tagValue as T[],
                                              getTagProps as any,
                                          )
                                    : (tagValue, getTagProps) =>
                                          tagValue.map((option, index) => (
                                              <Chip
                                                  {...getTagProps({ index })}
                                                  key={String(
                                                      getComparableOptionValue(
                                                          fallbackGetOptionValue(
                                                              option as T,
                                                          ),
                                                      ),
                                                  )}
                                                  label={fallbackGetOptionLabel(
                                                      option as T,
                                                  )}
                                                  size='small'
                                                  sx={{
                                                      fontSize,
                                                      lineHeight: 1.4,
                                                      height: 24,
                                                      fontFamily: 'Inter',
                                                  }}
                                              />
                                          ))
                            }
                            renderOption={
                                renderOption
                                    ? renderOption
                                    : (liProps, option, state) => (
                                          <li
                                              {...liProps}
                                              key={String(
                                                  getComparableOptionValue(
                                                      fallbackGetOptionValue(
                                                          option,
                                                      ),
                                                  ),
                                              )}
                                              style={{
                                                  ...optionTextSx,
                                              }}
                                          >
                                              <Checkbox
                                                  size='small'
                                                  sx={{
                                                      color: 'text.primary',
                                                      mr: '8px',
                                                      p: 0.5,
                                                  }}
                                                  checked={state.selected}
                                              />
                                              <span
                                                  style={{
                                                      opacity: 0.9,
                                                      fontSize,
                                                      fontFamily: 'Inter',
                                                      lineHeight: 1.4,
                                                  }}
                                              >
                                                  {fallbackGetOptionLabel(
                                                      option,
                                                  )}
                                              </span>
                                          </li>
                                      )
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={label}
                                    inputRef={ref}
                                    placeholder={activePlaceholder}
                                    error={hasError}
                                    helperText={helperText}
                                    sx={{
                                        '&& input::placeholder': {
                                            opacity: 1,
                                            color: 'text.secondary',
                                        },
                                    }}
                                    InputProps={params.InputProps}
                                    FormHelperTextProps={{
                                        sx: { fontSize, fontFamily: 'Inter' },
                                    }}
                                />
                            )}
                            popupIcon={<KeyboardArrowDown />}
                            slotProps={{
                                popper: {
                                    sx: {
                                        '& .MuiAutocomplete-paper .MuiAutocomplete-option':
                                            {
                                                opacity: 0.9,
                                                fontSize,
                                                fontFamily: 'Inter',
                                                lineHeight: 1.4,
                                                alignItems: 'center',
                                            },
                                    },
                                },
                            }}
                            PaperComponent={PaperComponent}
                        />
                    </FormControl>
                );
            }}
        />
    );
}

const FormMultiAutocomplete = FormMultiAutocompleteInner as <
    T extends BaseOptionShape,
>(
    p: FormMultiAutocompleteProps<T>,
) => JSX.Element;

export default FormMultiAutocomplete;
